import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
  Snackbar,
  CircularProgress,
  OutlinedInput,
  Chip,
  ListItemText,
  Checkbox,
  Switch,
  FormControlLabel,
  List,
  ListItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import DataTable from '../components/DataTable';
import { Subscription, Column, PaginationParams } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { subscriptionService } from '../services/subscriptionService';
import { tenantService } from '../services/tenantService';
import { mapApiSubscriptionToUi, mapUiToCreateApiModel, mapUiToUpdateApiModelPatch } from '../utils/subscriptionMapper';
import StatusChip from '../components/StatusChip';
import { PERMISSIONS } from '../utils/permissionAccess';


interface SubscriptionFormData {
  sponsorName: string;
  tenant: string;
  startDate: Date;
  endDate: Date;
  salesAgent: string;
  modules: string[];
  status: 'Active' | 'Inactive' | 'Pending' | 'Expired';
  adminFirstName: string;
  adminLastName: string;
  adminEmail: string;
}

// Helper function to get date one year from now
const getOneYearFromNow = (): Date => {
  const date = new Date();
  date.setFullYear(date.getFullYear() + 1);
  return date;
};

const Subscriptions: React.FC = () => {
  const { tokens, login, user } = useAuth();
  
  // Get user permissions
  const userPermissions = user?.permissions || [];
  
  // Helper function to check if user has a specific permission
  const hasPermission = (permission: string): boolean => {
    return userPermissions.some(userPerm => 
      userPerm.toLowerCase() === permission.toLowerCase() ||
      userPerm === permission
    );
  };
  
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [availableTenants, setAvailableTenants] = useState<any[]>([]);
  const [loadingTenants, setLoadingTenants] = useState<boolean>(false);
  const [tenantError, setTenantError] = useState<string | null>(null);
  const [subscriptionModules, setSubscriptionModules] = useState<Record<string, string[]>>({});
  const [subscriptionModulesFull, setSubscriptionModulesFull] = useState<Record<string, any[]>>({});
  const [loadingModules, setLoadingModules] = useState<Record<string, boolean>>({});
  
  // Edit Modules modal state
  const [openEditModulesModal, setOpenEditModulesModal] = useState<boolean>(false);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  const [availableModules, setAvailableModules] = useState<{ id: string; name: string }[]>([]);
  const [selectedModules, setSelectedModules] = useState<Array<{ moduleId: string; isActive: boolean }>>([]);
  const [loadingEditModules, setLoadingEditModules] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({
    open: false,
    message: '',
    severity: 'info',
  });
  const [pagination] = useState<PaginationParams>({
    page: 1,
    pageSize: 10,
  });

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [editingSubscription, setEditingSubscription] = useState<Subscription | null>(null);
  const [formData, setFormData] = useState<SubscriptionFormData>({
    sponsorName: '',
    tenant: '',
    startDate: new Date(),
    endDate: getOneYearFromNow(),
    salesAgent: '',
    modules: [],
    status: 'Active',
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
  });

  // Fetch modules for all subscriptions
  const fetchModulesForSubscriptions = async (subscriptionsList: Subscription[]) => {
    const modulesMap: Record<string, string[]> = {};
    const modulesFullMap: Record<string, any[]> = {};
    const loadingMap: Record<string, boolean> = {};
    
    // Initialize loading state for all subscriptions
    subscriptionsList.forEach(sub => {
      loadingMap[sub.id] = true;
    });
    setLoadingModules(loadingMap);
    
    // Fetch modules for each subscription in parallel
    const modulePromises = subscriptionsList.map(async (subscription) => {
      try {
        const response = await subscriptionService.getSubscriptionModules(subscription.id);
        if (response.succeeded && response.data) {
          // Store full module objects
          const fullModules = Array.isArray(response.data) ? response.data : [];
          
          // Extract module names for display
          const moduleNames = fullModules.map((item: any) => {
            // Handle different possible structures
            if (typeof item === 'string') {
              return item;
            }
            // If it's an object, extract the module name
            if (item && typeof item === 'object') {
              // Handle {isActive, module} structure where module might be a string or object
              if (item.module) {
                // If module is a string, use it directly; if it's an object, extract name
                return typeof item.module === 'string' 
                  ? item.module 
                  : (item.module.name || item.module.moduleName || String(item.module));
              }
              // Fallback to other possible property names
              return item.name || item.moduleName || String(item);
            }
            return String(item);
          });
          
          return { id: subscription.id, modules: moduleNames, fullModules };
        }
        return { id: subscription.id, modules: [], fullModules: [] };
      } catch (err) {
        console.error(`Error fetching modules for subscription ${subscription.id}:`, err);
        return { id: subscription.id, modules: [], fullModules: [] };
      }
    });
    
    const results = await Promise.all(modulePromises);
    
    // Update modules maps
    results.forEach(({ id, modules, fullModules }) => {
      modulesMap[id] = modules;
      modulesFullMap[id] = fullModules;
      loadingMap[id] = false;
    });
    
    setSubscriptionModules(modulesMap);
    setSubscriptionModulesFull(modulesFullMap);
    setLoadingModules(loadingMap);
  };

  // Load subscriptions from API
  const loadSubscriptions = async (params: PaginationParams = pagination) => {
    try {
      setLoading(true);
      setError(null);
      
      // Check if subscription endpoints are available
      if (!subscriptionService.isAuthenticated()) {
        setError('Authentication required. Please log in.');
        setLoading(false);
        return;
      }

      // Call the actual API endpoint
      const response = await subscriptionService.getSubscriptions(params);
      
      if (response.succeeded) {
        // response.data.items is an array of SubscriptionServiceResponseModel
        console.log('📋 Raw subscription data from API:', response.data);
        const subscriptionsData = response.data?.items || [];
        console.log('📋 Subscriptions items:', subscriptionsData);
        const mappedSubscriptions = subscriptionsData.map(mapApiSubscriptionToUi);
        console.log('📋 Mapped subscriptions:', mappedSubscriptions);
        
        setSubscriptions(mappedSubscriptions);
        
        // Fetch modules for each subscription
        fetchModulesForSubscriptions(mappedSubscriptions);
      } else {
        setError(response.errorDescription || 'Failed to load subscriptions');
      }
    } catch (err) {
      console.error('Error loading subscriptions:', err);
      setError('Failed to load subscriptions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Initialize service and load subscriptions on component mount
  useEffect(() => {
    // Initialize the subscription service with current tokens
    subscriptionService.initialize(tokens, login);
    tenantService.initialize(tokens, login);
    
    // Load subscriptions and tenants if we have tokens
    if (tokens) {
      loadSubscriptions();
      loadTenants(); // Load tenants on component mount
    } else {
      setLoading(false);
      setError('Authentication required. Please log in.');
    }
  }, [tokens, login]);

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };


  // Load tenants from API for the form dropdown
  const loadTenants = async (): Promise<{ id: string; name: string }[]> => {
    try {
      setLoadingTenants(true);
      setTenantError(null);
      console.log('🏢 Loading tenants for form dropdown...');
      const response = await tenantService.getTenants({ page: 1, pageSize: 100 });
      console.log('🏢 Tenants API Response:', response);
      
      if (response && (response.succeeded === true || response.succeeded === undefined)) {
        console.log('🏢 Full response structure:', JSON.stringify(response, null, 2));
        
        // Handle the correct API response structure
        let tenantsData = [];
        
        if (response.data && response.data.items && Array.isArray(response.data.items)) {
          // Correct paginated response structure
          tenantsData = response.data.items;
        } else {
          console.warn('🏢 Unexpected response structure:', response);
          setTenantError('Unexpected response format from server');
          setAvailableTenants([]);
          return [];
        }
        
        console.log('🏢 Extracted Tenants Data:', tenantsData);
        
        if (Array.isArray(tenantsData) && tenantsData.length > 0) {
          // Map API tenants to the format expected by the form
          const mappedTenants = tenantsData.map((tenant: any) => ({
            id: tenant.id,
            name: tenant.name
          }));
          console.log('🏢 Mapped Tenants for form:', mappedTenants);
          setAvailableTenants(mappedTenants);
          return mappedTenants;
        } else {
          console.warn('🏢 No tenant data found or empty array');
          setTenantError('No tenant data available');
          setAvailableTenants([]);
          return [];
        }
      } else {
        console.error('🏢 Failed to load tenants:', response);
        setTenantError(response?.errorDescription || 'Failed to load tenants from server');
        setAvailableTenants([]);
        return [];
      }
    } catch (err) {
      console.error('🏢 Error loading tenants:', err);
      setTenantError('Failed to load tenants. Please check your connection and try again.');
      setAvailableTenants([]);
      return [];
    } finally {
      setLoadingTenants(false);
    }
  };


  const columns: Column[] = [
    { id: 'sponsorName', label: 'Subscription Name', minWidth: 150, width: '15%', sortable: true },
    { id: 'tenant', label: 'Tenant Name', minWidth: 150, width: '15%', sortable: true },
    { 
      id: 'startDate', 
      label: 'Start Date', 
      minWidth: 120, 
      width: '12%',
      sortable: true,
      render: (value: Date) => value.toLocaleDateString(),
    },
    { 
      id: 'endDate', 
      label: 'End Date', 
      minWidth: 120, 
      width: '12%',
      sortable: true,
      render: (value: Date) => value.toLocaleDateString(),
    },
    { id: 'salesAgent', label: 'Sales Agent', minWidth: 120, width: '12%', sortable: true },
    { 
      id: 'modules', 
      label: 'Modules', 
      minWidth: 150, 
      width: '20%', 
      sortable: false,
      render: (_value: any, row: Subscription) => {
        const isLoading = loadingModules[row.id];
        const modules = subscriptionModules[row.id] || [];
        const fullModules = subscriptionModulesFull[row.id] || [];
        
        if (isLoading) {
          return <CircularProgress size={16} />;
        }
        
        if (modules.length === 0) {
          return <Typography variant="body2" color="text.secondary">No modules</Typography>;
        }
        
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {modules.map((moduleName, index) => {
              // Find the corresponding full module object to get isActive status
              // Try to match by module name or use index as fallback
              const fullModule = fullModules.find((item: any) => {
                if (item.module) {
                  const itemModuleName = typeof item.module === 'string' 
                    ? item.module 
                    : (item.module.name || item.module.moduleName || String(item.module));
                  return itemModuleName === moduleName;
                }
                return false;
              }) || fullModules[index];
              
              const isActive = fullModule?.isActive !== undefined ? fullModule.isActive : true;
              
              return (
                <Typography 
                  key={index} 
                  variant="body2" 
                  component="span"
                  sx={{ 
                    display: 'inline-block',
                    px: 1,
                    py: 0.5,
                    bgcolor: isActive ? 'primary.light' : 'grey.400',
                    color: isActive ? 'primary.contrastText' : 'grey.800',
                    borderRadius: 1,
                    fontSize: '0.75rem'
                  }}
                >
                  {moduleName}
                </Typography>
              );
            })}
          </Box>
        );
      },
    },
    { 
      id: 'status', 
      label: 'Status', 
      minWidth: 100, 
      width: '14%', 
      sortable: true,
      render: (value: string) => <StatusChip status={value as 'Active' | 'Inactive' | 'Pending' | 'Expired'} />,
    },
  ];

  const handleAdd = async (): Promise<void> => {
    setEditingSubscription(null);
    setFormData({
      sponsorName: '',
      tenant: '',
      startDate: new Date(),
      endDate: getOneYearFromNow(),
      salesAgent: '',
      modules: [],
      status: 'Active',
      adminFirstName: '',
      adminLastName: '',
      adminEmail: '',
    });
    
    // Tenants are already loaded on component mount
    setOpenModal(true);
  };

  const handleEdit = async (subscription: Subscription): Promise<void> => {
    setEditingSubscription(subscription);
    
    // Tenants are already loaded on component mount
    // Find tenant ID by name for form pre-population
    const tenantId = availableTenants.find(t => t.name === subscription.tenant)?.id || '';
    console.log(`🏢 Editing subscription with tenant name: "${subscription.tenant}"`);
    console.log(`🏢 Found tenant ID: "${tenantId}"`);
    console.log(`🏢 Available tenants:`, availableTenants);
    
    setFormData({
      sponsorName: subscription.sponsorName,
      tenant: tenantId,
      startDate: subscription.startDate,
      endDate: subscription.endDate,
      salesAgent: subscription.salesAgent,
      modules: subscription.modules,
      status: subscription.status,
      adminFirstName: (subscription as any).adminFirstName || '',
      adminLastName: (subscription as any).adminLastName || '',
      adminEmail: (subscription as any).adminEmail || '',
    });
    
    setOpenModal(true);
  };

  const handleDelete = async (subscription: Subscription): Promise<void> => {
    try {
      const response = await subscriptionService.deleteSubscription(subscription.id);
      if (response.succeeded) {
        showSnackbar('Subscription deleted successfully', 'success');
        loadSubscriptions(); // Reload the list
      } else {
        showSnackbar(response.errorDescription || 'Failed to delete subscription', 'error');
      }
    } catch (err) {
      console.error('Error deleting subscription:', err);
      showSnackbar('Failed to delete subscription. Please try again.', 'error');
    }
  };

  const handleToggleStatus = async (subscription: Subscription): Promise<void> => {
    try {
      const newStatus = subscription.status === 'Active' ? false : true;
      const response = await subscriptionService.toggleSubscriptionStatus(subscription.id, newStatus);
      if (response.succeeded) {
        showSnackbar(`Subscription ${newStatus ? 'activated' : 'deactivated'} successfully`, 'success');
        loadSubscriptions(); // Reload the list
      } else {
        showSnackbar(response.errorDescription || 'Failed to update subscription status', 'error');
      }
    } catch (err) {
      console.error('Error updating subscription status:', err);
      showSnackbar('Failed to update subscription status. Please try again.', 'error');
    }
  };

  // Edit Modules handlers
  const handleEditModules = async (subscription: Subscription): Promise<void> => {
    setSelectedSubscription(subscription);
    setOpenEditModulesModal(true);
    
    // Load current modules for this subscription
    await loadCurrentModules(subscription.id);
    // Collect available modules from all subscriptions
    await loadAvailableModules();
  };

  const loadCurrentModules = async (subscriptionId: string): Promise<void> => {
    try {
      setLoadingEditModules(true);
      const response = await subscriptionService.getSubscriptionModules(subscriptionId);
      
      if (response.succeeded && response.data) {
        const modules = Array.isArray(response.data) ? response.data : [];
        // Map to the format we need: { moduleId, isActive }
        const mappedModules = modules.map((item: any) => {
          // Extract module ID from the structure
          let moduleId = '';
          if (item.module) {
            moduleId = typeof item.module === 'string' 
              ? item.module 
              : (item.module.id || item.module.moduleId || String(item.module));
          } else if (item.id) {
            moduleId = item.id;
          } else if (item.moduleId) {
            moduleId = item.moduleId;
          }
          
          return {
            moduleId,
            isActive: item.isActive !== undefined ? item.isActive : true
          };
        }).filter((m: any) => m.moduleId); // Filter out invalid entries
        
        setSelectedModules(mappedModules);
      } else {
        setSelectedModules([]);
      }
    } catch (err) {
      console.error('Error loading current modules:', err);
      setSelectedModules([]);
    } finally {
      setLoadingEditModules(false);
    }
  };

  const loadAvailableModules = async (): Promise<void> => {
    try {
      // Collect unique modules from all subscriptions
      const allModulesMap = new Map<string, { id: string; name: string }>();
      
      // Get modules from all subscriptions we've loaded
      Object.values(subscriptionModulesFull).forEach((modules: any[]) => {
        modules.forEach((item: any) => {
          let moduleId = '';
          let moduleName = '';
          
          if (item.module) {
            if (typeof item.module === 'string') {
              moduleId = item.module;
              moduleName = item.module;
            } else {
              moduleId = item.module.id || item.module.moduleId || String(item.module);
              moduleName = item.module.name || item.module.moduleName || moduleId;
            }
          } else if (item.id) {
            moduleId = item.id;
            moduleName = item.name || item.moduleName || moduleId;
          } else if (item.moduleId) {
            moduleId = item.moduleId;
            moduleName = item.name || item.moduleName || moduleId;
          }
          
          if (moduleId && !allModulesMap.has(moduleId)) {
            allModulesMap.set(moduleId, { id: moduleId, name: moduleName || moduleId });
          }
        });
      });
      
      // If we don't have enough modules, try to get from current subscription
      if (allModulesMap.size === 0 && selectedSubscription) {
        const response = await subscriptionService.getSubscriptionModules(selectedSubscription.id);
        if (response.succeeded && response.data) {
          const modules = Array.isArray(response.data) ? response.data : [];
          modules.forEach((item: any) => {
            let moduleId = '';
            let moduleName = '';
            
            if (item.module) {
              if (typeof item.module === 'string') {
                moduleId = item.module;
                moduleName = item.module;
              } else {
                moduleId = item.module.id || item.module.moduleId || String(item.module);
                moduleName = item.module.name || item.module.moduleName || moduleId;
              }
            }
            
            if (moduleId && !allModulesMap.has(moduleId)) {
              allModulesMap.set(moduleId, { id: moduleId, name: moduleName || moduleId });
            }
          });
        }
      }
      
      setAvailableModules(Array.from(allModulesMap.values()));
    } catch (err) {
      console.error('Error loading available modules:', err);
      setAvailableModules([]);
    }
  };

  const handleModuleSelectionChange = (event: any): void => {
    const value = event.target.value;
    const selectedIds = typeof value === 'string' ? value.split(',') : value;
    
    // Update selected modules, keeping isActive state for existing ones
    const updatedModules = selectedIds.map((moduleId: string) => {
      const existing = selectedModules.find(m => m.moduleId === moduleId);
      return existing || { moduleId, isActive: false };
    });
    
    setSelectedModules(updatedModules);
  };

  const handleModuleActiveToggle = (moduleId: string): void => {
    setSelectedModules(prev => 
      prev.map(m => 
        m.moduleId === moduleId 
          ? { ...m, isActive: !m.isActive }
          : m
      )
    );
  };

  const handleSaveModules = async (): Promise<void> => {
    if (!selectedSubscription) return;

    try {
      setLoadingEditModules(true);
      
      // Get current modules from API
      const currentResponse = await subscriptionService.getSubscriptionModules(selectedSubscription.id);
      const currentModules = currentResponse.succeeded && currentResponse.data 
        ? (Array.isArray(currentResponse.data) ? currentResponse.data : [])
        : [];
      
      // Extract current module IDs and their isActive state
      const currentModuleMap = new Map<string, { moduleId: string; isActive: boolean }>();
      currentModules.forEach((item: any) => {
        let moduleId = '';
        if (item.module) {
          moduleId = typeof item.module === 'string' 
            ? item.module 
            : (item.module.id || item.module.moduleId || String(item.module));
        } else if (item.id) {
          moduleId = item.id;
        } else if (item.moduleId) {
          moduleId = item.moduleId;
        }
        
        if (moduleId) {
          currentModuleMap.set(moduleId, {
            moduleId,
            isActive: item.isActive !== undefined ? item.isActive : true
          });
        }
      });
      
      // Calculate changes
      const selectedModuleMap = new Map(selectedModules.map(m => [m.moduleId, m]));
      
      // Find modules to add (in selected but not in current)
      const toAdd = selectedModules
        .filter(m => !currentModuleMap.has(m.moduleId))
        .map(m => ({ moduleId: m.moduleId, isActive: m.isActive }));
      
      // Find modules to update (in both, but isActive changed)
      const toUpdate = selectedModules
        .filter(m => {
          const current = currentModuleMap.get(m.moduleId);
          return current && current.isActive !== m.isActive;
        })
        .map(m => ({ moduleId: m.moduleId, isActive: m.isActive }));
      
      // Find modules to remove (in current but not in selected)
      const toRemove = Array.from(currentModuleMap.keys())
        .filter(id => !selectedModuleMap.has(id));
      
      // Only make API call if there are changes
      if (toAdd.length === 0 && toUpdate.length === 0 && toRemove.length === 0) {
        showSnackbar('No changes to save', 'info');
        setOpenEditModulesModal(false);
        return;
      }
      
      const payload: {
        add?: Array<{ moduleId: string; isActive: boolean }>;
        update?: Array<{ moduleId: string; isActive: boolean }>;
        remove?: string[];
      } = {};
      
      if (toAdd.length > 0) payload.add = toAdd;
      if (toUpdate.length > 0) payload.update = toUpdate;
      if (toRemove.length > 0) payload.remove = toRemove;
      
      const response = await subscriptionService.updateSubscriptionModules(selectedSubscription.id, payload);
      
      if (response.succeeded) {
        showSnackbar('Modules updated successfully', 'success');
        setOpenEditModulesModal(false);
        loadSubscriptions(); // Reload to refresh modules display
      } else {
        showSnackbar(response.errorDescription || 'Failed to update modules', 'error');
      }
    } catch (err) {
      console.error('Error updating modules:', err);
      showSnackbar('Failed to update modules. Please try again.', 'error');
    } finally {
      setLoadingEditModules(false);
    }
  };

  const handleSave = async (): Promise<void> => {
    try {
      if (editingSubscription) {
        // Update existing subscription - use PATCH with only changed fields
        const updateData = mapUiToUpdateApiModelPatch(editingSubscription, formData);
        const response = await subscriptionService.updateSubscription(editingSubscription.id, updateData);
        
        if (response.succeeded) {
          showSnackbar('Subscription updated successfully', 'success');
          loadSubscriptions(); // Reload the list
          setOpenModal(false);
        } else {
          showSnackbar(response.errorDescription || 'Failed to update subscription', 'error');
        }
      } else {
        // Create new subscription
        const createData = mapUiToCreateApiModel(formData);
        const response = await subscriptionService.createSubscription(createData);
        
        if (response.succeeded) {
          showSnackbar('Subscription created successfully', 'success');
          loadSubscriptions(); // Reload the list
          setOpenModal(false);
        } else {
          showSnackbar(response.errorDescription || 'Failed to create subscription', 'error');
        }
      }
    } catch (err) {
      console.error('Error saving subscription:', err);
      showSnackbar('Failed to save subscription. Please try again.', 'error');
    }
  };

  const handleCancel = (): void => {
    setOpenModal(false);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" gutterBottom>
        Manage Subscriptions
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataTable
          data={subscriptions}
          columns={columns}
          onAdd={hasPermission(PERMISSIONS.CREATE_SUBSCRIPTION) ? handleAdd : undefined}
          onEdit={hasPermission(PERMISSIONS.EDIT_SUBSCRIPTION) ? handleEdit : undefined}
          onDelete={hasPermission(PERMISSIONS.DELETE_SUBSCRIPTION) ? handleDelete : undefined}
          onToggleStatus={hasPermission(PERMISSIONS.DISABLE_SUBSCRIPTION) ? handleToggleStatus : undefined}
          onEditModules={hasPermission(PERMISSIONS.EDIT_SUBSCRIPTION_MODULES) ? handleEditModules : undefined}
          title="Subscriptions"
          searchFields={['sponsorName', 'tenant', 'salesAgent']}
        />
      )}

      <Dialog open={openModal} onClose={handleCancel} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingSubscription ? 'Edit Subscription' : 'Add New Subscription'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Subscription Name"
              value={formData.sponsorName}
              onChange={(e) => setFormData({ ...formData, sponsorName: e.target.value })}
              fullWidth
              required
              disabled={!!editingSubscription}
            />
            
            <FormControl fullWidth required error={!!tenantError}>
              <InputLabel>Tenant</InputLabel>
              <Select
                value={formData.tenant}
                onChange={(e) => setFormData({ ...formData, tenant: e.target.value })}
                label="Tenant"
                disabled={!!editingSubscription || loadingTenants || !!tenantError}
              >
                {loadingTenants ? (
                  <MenuItem disabled>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">
                        Loading tenants...
                      </Typography>
                    </Box>
                  </MenuItem>
                ) : tenantError ? (
                  <MenuItem disabled>
                    <Typography variant="body2" color="error">
                      {tenantError}
                    </Typography>
                  </MenuItem>
                ) : availableTenants.length === 0 ? (
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">
                      No tenants available
                    </Typography>
                  </MenuItem>
                ) : (
                  availableTenants.map((tenant) => (
                    <MenuItem key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </MenuItem>
                  ))
                )}
              </Select>
              {tenantError && (
                <FormHelperText error>
                  {tenantError}
                </FormHelperText>
              )}
            </FormControl>
            
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <DatePicker
                  label="Start Date"
                  value={formData.startDate}
                  onChange={(newValue) => setFormData({ ...formData, startDate: newValue || new Date() })}
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
                <DatePicker
                  label="End Date"
                  value={formData.endDate}
                  onChange={(newValue) => setFormData({ ...formData, endDate: newValue || new Date() })}
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
              </Box>
            </LocalizationProvider>
            
            <TextField
              label="Sales Agent"
              value={formData.salesAgent}
              onChange={(e) => setFormData({ ...formData, salesAgent: e.target.value })}
              fullWidth
              required
            />
            
            {/* Modules field removed - not available in API response */}
            
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as SubscriptionFormData['status'] })}
                label="Status"
                renderValue={(value) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StatusChip status={value as 'Active' | 'Inactive' | 'Pending' | 'Expired'} size="small" />
                  </Box>
                )}
              >
                <MenuItem value="Active">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StatusChip status="Active" size="small" />
                    <span>Active</span>
                  </Box>
                </MenuItem>
                <MenuItem value="Inactive">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StatusChip status="Inactive" size="small" />
                    <span>Inactive</span>
                  </Box>
                </MenuItem>
                <MenuItem value="Pending">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StatusChip status="Pending" size="small" />
                    <span>Pending</span>
                  </Box>
                </MenuItem>
                <MenuItem value="Expired">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StatusChip status="Expired" size="small" />
                    <span>Expired</span>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
            
            {!editingSubscription && (
              <>
                <Box sx={{ mt: 2, mb: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    Administrator
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="First Name"
                    value={formData.adminFirstName}
                    onChange={(e) => setFormData({ ...formData, adminFirstName: e.target.value })}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Last Name"
                    value={formData.adminLastName}
                    onChange={(e) => setFormData({ ...formData, adminLastName: e.target.value })}
                    fullWidth
                    required
                  />
                </Box>
                
                <TextField
                  label="Email"
                  type="email"
                  value={formData.adminEmail}
                  onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
                  fullWidth
                  required
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Modules Modal */}
      <Dialog open={openEditModulesModal} onClose={() => setOpenEditModulesModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Edit Modules - {selectedSubscription?.sponsorName}
        </DialogTitle>
        <DialogContent>
          {loadingEditModules ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>Modules</InputLabel>
                <Select
                  multiple
                  value={selectedModules.map(m => m.moduleId)}
                  onChange={handleModuleSelectionChange}
                  input={<OutlinedInput label="Modules" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((moduleId) => {
                        const module = availableModules.find(m => m.id === moduleId);
                        const selectedModule = selectedModules.find(m => m.moduleId === moduleId);
                        return (
                          <Chip
                            key={moduleId}
                            label={`${module?.name || moduleId} ${selectedModule?.isActive ? '(Active)' : '(Inactive)'}`}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {availableModules.map((module) => (
                    <MenuItem key={module.id} value={module.id}>
                      <Checkbox checked={selectedModules.some(m => m.moduleId === module.id)} />
                      <ListItemText primary={module.name} />
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Select modules for this subscription
                </FormHelperText>
              </FormControl>

              {selectedModules.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Module Status
                  </Typography>
                  <List dense>
                    {selectedModules.map((module) => {
                      const moduleInfo = availableModules.find(m => m.id === module.moduleId);
                      return (
                        <ListItem
                          key={module.moduleId}
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            mb: 1,
                            py: 1,
                          }}
                        >
                          <ListItemText
                            primary={moduleInfo?.name || module.moduleId}
                            secondary={module.isActive ? 'Active' : 'Inactive'}
                          />
                          <FormControlLabel
                            control={
                              <Switch
                                checked={module.isActive}
                                onChange={() => handleModuleActiveToggle(module.moduleId)}
                                size="small"
                              />
                            }
                            label={module.isActive ? 'Active' : 'Inactive'}
                            labelPlacement="start"
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditModulesModal(false)} color="secondary">
            Cancel
          </Button>
          <Button 
            onClick={handleSaveModules} 
            variant="contained" 
            color="primary"
            disabled={loadingEditModules}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Subscriptions; 